<script setup>
import { computed, ref, watch, onUnmounted } from 'vue';
import { useI18n } from 'vue-i18n';
import { useStore } from 'vuex';
import { savedPVC, forgetPVC } from '../services/privacy';
import config from '../config';
import { auxRequest, formatZat, publicAuxResult, blockConfirmationPercent } from '../services/aux';
import { mdiChevronDown, mdiAlertCircleOutline, mdiRefresh } from '@mdi/js';

const props = defineProps({ kind: { type: String, required: true }, login: { type: String, default: '' }, showBalances: Boolean, selectedCoin: { type: String, default: '' } });
const { t } = useI18n();
const store = useStore();
const coins = config.auxCoins || ['WEC'];
const coin = ref(coins.includes(props.selectedCoin) ? props.selectedCoin : coins[0]);
const page = ref(1);
const result = ref({ items: [], total: 0, balances: null });
const busy = ref(false);
const error = ref(false);
let controller;
let timer;
let generation = 0;
let disposed = false;
const pages = computed(() => Math.max(1, Math.ceil(result.value.total / 20)));
const isBlockTable = computed(() => props.kind !== 'payments');
const isRewardTable = computed(() => props.kind === 'rewards');
const columnCount = computed(() => isRewardTable.value ? 8 : isBlockTable.value ? 7 : props.login ? 3 : 4);
const confirmationDepth = computed(() => config.auxBlockConfirmations?.[coin.value]);
function confirmationPercent(item) { return blockConfirmationPercent(item, confirmationDepth.value); }
function status(value) { return t(`aux_status_${['candidates', 'immature', 'matured', 'orphan', 'paid'].includes(value) ? value : 'unknown'}`); }
function transactionLink(item) {
  const base = config.auxExplorers?.[item.coin]?.tx;
  return base && item.txid ? `${base}${encodeURIComponent(item.txid)}` : null;
}
function blockLink(item) {
  const base = config.auxExplorers?.[item.coin]?.hash;
  return base && item.hash ? `${base}${encodeURIComponent(item.hash)}` : null;
}
async function load(clear = false) {
  clearTimeout(timer);
  controller?.abort();
  const current = ++generation;
  controller = new AbortController();
  busy.value = true;
  if (clear) {
    error.value = false;
    result.value = { items: [], total: 0, balances: null };
  }
  const path = props.kind === 'blocks' ? 'aux/blocks' : `account/${encodeURIComponent(props.login)}/aux/${props.kind}`;
  const query = new URLSearchParams({ coin: coin.value, offset: String((page.value - 1) * 20), limit: '20' });
  try {
    const pvc = props.login && store.state.account?.miner === props.login && store.state.account.privateView ? savedPVC(props.login) : '';
    const data = await auxRequest(config.auxApiBase || '/api/v1', `${path}?${query}`, { signal: controller.signal, pvc });
    if (current === generation) {
      result.value = publicAuxResult(data, props.kind);
      error.value = false;
    }
  } catch (e) {
    if (current === generation && e.name !== 'AbortError') {
      error.value = true;
      result.value = { items: [], total: 0, balances: null };
      if (props.login && [401, 403, 404, 429].includes(e.status)) {
        if ([401, 404].includes(e.status)) forgetPVC(props.login);
        store.commit('REVOKE_PRIVATE_VIEW');
        store.commit('SET_ACCOUNT_VIEW_ERROR', { error: e.message, retryAfter: e.retryAfter });
      }
    }
  } finally {
    if (current === generation && !disposed) {
      busy.value = false;
      timer = setTimeout(() => load(), error.value ? 60000 : 10000);
    }
  }
}
watch(() => props.selectedCoin, value => { if (coins.includes(value)) coin.value = value; });
watch(() => [props.kind, props.login, coin.value, page.value], (next, previous) => {
  if (previous && next.slice(0, 3).some((value, index) => value !== previous[index]) && page.value !== 1) {
    page.value = 1;
    return;
  }
  load(true);
}, { immediate: true });
onUnmounted(() => { disposed = true; generation++; controller?.abort(); clearTimeout(timer); });
</script>

<template>
  <div v-if="!selectedCoin && coins.length > 1" class="mb-3 d-flex align-center ga-3">
    <v-select v-if="coins.length > 1 && !selectedCoin" v-model="coin" :items="coins" :label="$t('aux_coin')"
      :menu-icon="mdiChevronDown" hide-details density="compact" style="max-width: 180px" />
  </div>
  <div v-if="error" class="aux-message" role="status">
    <v-icon :icon="mdiAlertCircleOutline" size="18" />
    <span>{{ $t('aux_error_aux_unavailable', { coin }) }}</span>
    <v-btn variant="outlined" size="small" class="aux-retry" :prepend-icon="mdiRefresh" :loading="busy"
      @click="load()">{{ $t('aux_retry') }}</v-btn>
  </div>
  <template v-if="showBalances && result.balances">
    <v-row dense class="mb-3">
      <v-col v-for="field in ['immatureZat', 'balanceZat', 'pendingZat', 'paidZat']" :key="field" cols="12" sm="6"
        md="3">
        <v-card variant="tonal" class="pa-3">
          <div class="text-caption text-medium-emphasis">{{ $t(`aux_${field}`) }}</div>
          <div class="text-monospace mt-1">{{ formatZat(result.balances[field]) }} {{ coin }}</div>
        </v-card>
      </v-col>
    </v-row>
  </template>
  <h4 v-if="kind === 'blocks' && !login && confirmationDepth && !error" class="mb-2">{{ $t('aux_blocks_confirmations', {
    count: confirmationDepth
  }) }}</h4>
  <v-table v-if="!error" class="border rounded aux-table" density="comfortable">
    <thead>
      <tr>
        <th v-if="isBlockTable">{{ $t('height') }}</th>
        <th v-if="isBlockTable && (!login || isRewardTable)">{{ $t('type') }}</th>
        <th>{{ $t('time') }}</th>
        <th v-if="!login">{{ $t('miner') }}</th>
        <th v-if="isBlockTable && login">{{ $t('worker') }}</th>
        <th v-if="isBlockTable">{{ $t('solution') }}</th>
        <th v-if="isBlockTable && login && !isRewardTable">{{ $t('aux_confirmations') }}</th>
        <th v-if="isBlockTable">{{ $t('status') }}</th>
        <th v-if="isRewardTable">{{ $t('percent') }}</th>
        <th :class="{ 'text-end': isBlockTable }">{{ $t(kind === 'payments' ? 'amount' : 'reward') }}
          <small v-if="kind === 'payments'" class="text-muted">{{ coin }}</small>
          <span v-else-if="!isRewardTable" class="text-medium-emphasis">· {{ coin }}</span>
        </th>
        <th v-if="kind === 'payments'">TX</th>
      </tr>
    </thead>
    <tbody>
      <tr v-for="(item, index) in result.items" :key="item.hash || `${item.time}-${index}`">
        <td v-if="isBlockTable" class="text-no-wrap aux-number" :title="item.hash"><a v-if="blockLink(item)"
            :href="blockLink(item)" target="_blank" rel="noopener noreferrer">{{ item.height }}</a><span v-else>{{
              item.height }}</span></td>
        <td v-if="isBlockTable && (!login || isRewardTable)">Block</td>
        <td class="text-no-wrap">{{ $filters.time(item.time) }}</td>
        <td v-if="!login"><router-link v-if="item.miner" class="aux-miner text-monospace" :title="item.miner"
            :to="`/account/${item.miner}`">{{ item.miner }}</router-link><span v-else class="text-medium-emphasis">{{
              $t('mp_private_miner') }}</span></td>
        <td v-if="isBlockTable && login" class="aux-worker">{{ item.worker || '—' }}</td>
        <td v-if="isBlockTable" class="text-no-wrap"><span v-if="Number.isFinite(item.shareDifficulty)"
            v-html="$filters.difficulty(item.shareDifficulty, 3)"></span><span v-else>—</span></td>
        <td v-if="isBlockTable && login && !isRewardTable" class="aux-number">{{ item.confirm ?? '—' }}</td>
        <td v-if="isBlockTable" class="text-no-wrap"
          :title="isBlockTable ? `${$t('aux_confirmations')}: ${item.confirm ?? '—'}` : undefined">
          <v-progress-linear v-if="isBlockTable && confirmationPercent(item) !== null" color="rgba(255,255,255,.1)"
            :model-value="confirmationPercent(item)" height="18" class="rounded aux-maturity"
            :aria-label="$t('aux_confirmations')"><small>{{ Math.ceil(confirmationPercent(item))
              }}%</small></v-progress-linear>
          <span v-else :class="item.orphan ? 'text-red' : ''">{{ status(item.orphan ? 'orphan' : item.status) }}</span>
        </td>
        <!-- WEC SOLO rewards belong entirely to the finder; creditZat already excludes the pool fee. -->
        <td v-if="isRewardTable" class="text-no-wrap aux-number">{{ $filters.round(1, 6) }}</td>
        <td class="text-no-wrap aux-number"
          :class="[{ 'text-end': isBlockTable }, item.orphan ? 'text-red' : isBlockTable && ['candidates', 'immature'].includes(item.status) ? 'text-grey' : '']">
          {{ formatZat(kind === 'payments' ? item.amountZat : item.orphan ? '0' : kind === 'rewards' ? item.creditZat :
            item.rewardZat) }}</td>
        <td v-if="kind === 'payments'" class="aux-transaction"><a v-if="transactionLink(item)"
            :href="transactionLink(item)" target="_blank" rel="noopener noreferrer">{{ item.txid }}</a><span v-else>{{
              item.txid || '—' }}</span></td>
      </tr>
      <tr v-if="!result.items.length">
        <td :colspan="columnCount" class="text-center py-6 text-medium-emphasis">{{ $t(busy ? 'aux_loading' : 'no_data')
          }}</td>
      </tr>
    </tbody>
  </v-table>
  <v-pagination v-if="pages > 1" v-model="page" :length="pages" :total-visible="5" density="compact" class="mt-3" />
</template>

<style scoped>
.aux-message {
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 8px 12px;
  padding: 10px 12px;
  border: 1px solid rgba(255, 167, 38, .24);
  border-radius: 4px;
  background: rgba(255, 167, 38, .06);
  color: #ffb74d;
  font-size: 13px;
}

.aux-retry {
  margin-left: auto;
  text-transform: none;
  letter-spacing: 0;
}

.aux-number {
  font-variant-numeric: tabular-nums;
}

.aux-miner {
  white-space: nowrap;
}

.aux-worker {
  max-width: 150px;
  overflow-wrap: anywhere;
}

.aux-transaction {
  max-width: 360px;
  overflow-wrap: anywhere;
}

.aux-maturity {
  min-width: 68px;
}
</style>
